<template>
    <label :title="!readOnly ? $t('user.change-your-avatar') : ''" @click="takePicture">
        <span class="inline-block relative">
            <img
                class="text-center inline-block rounded-full"
                :class="small ? 'h-20 w-20' : 'h-40 w-40'"
                :src="avatar"
                alt="avatar"
            />

            <span
                v-if="!readOnly"
                class="absolute bottom-0 right-0 block h-6 w-6 rounded-full text-white shadow-solid bg-dark-light"
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                </svg>
            </span>
        </span>
    </label>

    <!-- Teleported: #topWindow's transform would otherwise anchor these fixed layers to the whole
         page instead of the screen. -->
    <teleport to="#root-fullscreen">
        <div
            v-show="showModal"
            class="overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-hidden focus:outline-hidden justify-center items-center flex p-4 rounded-lg"
        >
            <div class="relative w-full max-w-2xl rounded-lg ring-3 ring-dark-lightest">
                <!--content-->
                <div
                    class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-dark-base outline-hidden focus:outline-hidden"
                >
                    <!--header-->
                    <RawHeader
                        :back-text="$t('nav.cancel')"
                        :back-action="() => closeModal()"
                        :primary="false"
                        :withIcons="false"
                        :addPadding="false"
                    >
                        {{ $t('user.crop-avatar') }}
                    </RawHeader>
                    <!--body-->
                    <div class="h-full overflow-y-auto">
                        <div class="m-5">
                            <div ref="imageContainer" class="cropper-container"></div>
                            <div class="mt-5 rounded-md shadow-2xs">
                                <Button :action="onCrop"> {{ $t('form.save-close') }} </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- data-app-modal-open freezes the page behind the crop dialog (app.css). -->
        <div v-if="showModal" data-app-modal-open class="opacity-25 fixed inset-0 z-40 bg-black"></div>
    </teleport>
</template>

<style scoped>
.cropper-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
}
.cropper-container ::v-deep(cropper-canvas) {
    height: 300px;
    width: 300px;
}
</style>

<script setup>
import { ref } from 'vue'
import { Camera, CameraResultType } from '@capacitor/camera'
import { useI18n } from 'vue-i18n'
import Cropper from 'cropperjs'

import RawHeader from '@/components/RawHeader.vue'
import Button from '@/components/buttons/Primary.vue'

const props = defineProps({
    avatar: {
        type: String,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    small: {
        type: Boolean,
        default: false,
    },
})
const emits = defineEmits(['update:avatar'])

const showModal = ref(false)
const cropperRef = ref(null)
const url = ref('')
const imageContainer = ref(null)
const { t } = useI18n()

const closeModal = () => {
    cropperRef.value = null
    showModal.value = false
}
const takePicture = async () => {
    if (!props.readOnly) {
        try {
            const imageChoosed = await Camera.getPhoto({
                // Web only: the file picker, not the webcam modal that needs @ionic/pwa-elements.
                webUseInput: true,
                quality: 100,
                allowEditing: false,
                resultType: CameraResultType.DataUrl,
                promptLabelHeader: t('avatar.promptLabelHeader'), // 'Photo',
                promptLabelCancel: t('avatar.promptLabelCancel'), // 'Cancel',
                promptLabelPhoto: t('avatar.promptLabelPhoto'), // 'From Photos',
                promptLabelPicture: t('avatar.promptLabelPicture'), // 'Take Picture',
            })
            url.value = imageChoosed.dataUrl
            showModal.value = true
            modalReady()
        } catch (e) {
            console.log('avatar', e)
        }
    }
}

const modalReady = () => {
    let img = document.createElement('img')
    img.id = 'image'

    img.src = url.value

    // clean result before
    imageContainer.value.innerHTML = ''
    // append new image
    imageContainer.value.appendChild(img)

    cropperRef.value = new Cropper(img)

    // {
    //     viewMode: 1,
    //     dragMode: 'move',
    //     aspectRatio: 1,
    //     autoCropArea: 1,
    //     minContainerWidth: 300,
    //     minContainerHeight: 300,
    //     center: false,
    //     zoomOnWheel: true,
    //     zoomOnTouch: true,
    //     cropBoxMovable: false,
    //     cropBoxResizable: true,
    // })
}
const onCrop = async () => {
    let canvas = await cropperRef.value.getCropperSelection().$toCanvas({
        width: 1024,
        height: 1024,
    })
    // cropperRef.value.getCroppedCanvas({
    //     maxWidth: 1024,
    //     maxHeight: 1024,
    //     imageSmoothingEnabled: true,
    //     imageSmoothingQuality: 'high',
    // })
    canvas.toBlob((blob) => {
        let file = new File([blob], 'avatar.png')
        emits('update:avatar', file)
        closeModal()
    })
}
</script>
